import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  AppState,
  Company,
  CostCenter,
  CostCenterFilter as CostCenterFilterValue,
  Field,
  FixedCost,
  Row,
  SaveStatus,
  View,
} from "./types.ts";
import { hasInvalidDates, isoWeek, newId, todayISO } from "./format.ts";
import { loadState } from "./api.ts";
import { Toaster, toast } from "sonner";
import { useHotkeys } from "react-hotkeys-hook";
import { useAutosave } from "./useAutosave.ts";
import {
  fieldFromElement,
  focusRowField,
  nextSiblingRow,
  prevSiblingRow,
} from "./focus.ts";
import { Header } from "./components/Header.tsx";
import { ImportDialog } from "./components/ImportDialog.tsx";
import { FixedCostImportDialog } from "./components/FixedCostImportDialog.tsx";
import { planImportFromText, type ImportPlan } from "./invoiceCsv.ts";
import { StartBalance } from "./components/StartBalance.tsx";
import { LedgerTable } from "./components/LedgerTable.tsx";
import { SnoozedTable } from "./components/SnoozedTable.tsx";
import { FixedCostsTable } from "./components/FixedCostsTable.tsx";
import { FixedCostsSummary } from "./components/FixedCostsSummary.tsx";
import { CostCenterFilter } from "./components/CostCenterFilter.tsx";
import { CostCentersDialog } from "./components/CostCentersDialog.tsx";
import { CompaniesDialog } from "./components/CompaniesDialog.tsx";

const MAX_UNDO = 5;

type PendingFocus =
  | { kind: "startBalance" }
  | { kind: "row"; rowId: string; field: Field };

const START_BALANCE_FOCUS: PendingFocus = { kind: "startBalance" };

type Props = {
  companyId: string;
  companies: Company[];
  view: View;
  onViewChange: (view: View) => void;
  onSwitchCompany: (companyId: string) => void;
  onCreateCompany: (name: string) => Promise<Company>;
  onRenameCompany: (id: string, name: string) => Promise<void>;
};

export function CompanyApp({
  companyId,
  companies,
  view,
  onViewChange,
  onSwitchCompany,
  onCreateCompany,
  onRenameCompany,
}: Props) {
  const [state, setState] = useState<AppState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusedDateRowId, setFocusedDateRowId] = useState<string | null>(null);
  const [pendingFocus, setPendingFocus] = useState<PendingFocus | null>(null);
  const [pendingImport, setPendingImport] = useState<{
    fileName: string;
    plan: ImportPlan;
  } | null>(null);
  const [costCenterFilter, setCostCenterFilter] =
    useState<CostCenterFilterValue>("all");
  const [pinnedCostIds, setPinnedCostIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [managingCostCenters, setManagingCostCenters] = useState(false);
  const [managingCompanies, setManagingCompanies] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  const [importingFixedCosts, setImportingFixedCosts] = useState(false);
  const costCenterFilterRef = useRef(costCenterFilter);
  const modalOpenRef = useRef(false);
  modalOpenRef.current =
    managingCostCenters ||
    managingCompanies ||
    importingFixedCosts ||
    pendingImport !== null;
  const topRef = useRef<HTMLDivElement>(null);
  const manageCostCentersRef = useRef<HTMLButtonElement>(null);
  const manageCompaniesRef = useRef<HTMLButtonElement>(null);
  const undoRef = useRef<Array<{ row: Row; index: number }>>([]);
  const stateRef = useRef(state);
  stateRef.current = state;
  const viewRef = useRef(view);
  viewRef.current = view;

  const reload = useCallback(
    async (signal?: { cancelled: boolean }) => {
      const next = await loadState(companyId);
      if (signal?.cancelled) return;
      setState(next);
      setDirty(false);
      undoRef.current = [];
      setFocusedDateRowId(null);
      setPendingFocus(START_BALANCE_FOCUS);
    },
    [companyId],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void reload(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [reload]);

  const handleSavedVersion = useCallback(
    (version: number, hadConcurrentMutation: boolean) => {
      setState((s) => (s ? { ...s, version } : s));
      if (!hadConcurrentMutation) setDirty(false);
    },
    [],
  );

  const { forceSave, noteMutation } = useAutosave({
    companyId,
    state,
    dirty,
    onSavedVersion: handleSavedVersion,
    onConflict: () => {
      void reload();
    },
    onError: (msg) => {
      toast.error(msg);
      setPendingSwitch(null);
    },
    onSavingChange: setSaving,
  });

  const mutate = useCallback(
    (updater: (s: AppState) => AppState) => {
      setState((s) => (s ? updater(s) : s));
      setDirty(true);
      noteMutation();
    },
    [noteMutation],
  );

  const patchRow = useCallback(
    (id: string, patch: Partial<Row>) => {
      mutate((s) => ({
        ...s,
        rows: s.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      }));
    },
    [mutate],
  );

  const addRow = useCallback(
    (dueDate?: string, afterRowId?: string) => {
      const date = dueDate || todayISO();
      const id = newId();
      const row: Row = {
        id,
        week: isoWeek(date),
        dueDate: date,
        supplier: "",
        amount: 0,
        note: "",
        snoozed: false,
      };
      mutate((s) => {
        const rows = [...s.rows];
        if (afterRowId) {
          const idx = rows.findIndex((r) => r.id === afterRowId);
          if (idx >= 0) rows.splice(idx + 1, 0, row);
          else rows.push(row);
        } else {
          rows.push(row);
        }
        return { ...s, rows };
      });
      setPendingFocus({ kind: "row", rowId: id, field: "supplier" });
    },
    [mutate],
  );

  const deleteRow = useCallback(
    (id: string) => {
      setState((s) => {
        if (!s) return s;
        const idx = s.rows.findIndex((r) => r.id === id);
        if (idx < 0) return s;
        const removed = s.rows[idx]!;
        undoRef.current.push({ row: removed, index: idx });
        if (undoRef.current.length > MAX_UNDO) undoRef.current.shift();
        return { ...s, rows: s.rows.filter((r) => r.id !== id) };
      });
      setDirty(true);
      noteMutation();
    },
    [noteMutation],
  );

  const undoDelete = useCallback(() => {
    const entry = undoRef.current.pop();
    if (!entry) return false;
    mutate((s) => {
      const insertAt = Math.min(entry.index, s.rows.length);
      const rows = [...s.rows];
      rows.splice(insertAt, 0, entry.row);
      return { ...s, rows };
    });
    setPendingFocus({ kind: "row", rowId: entry.row.id, field: "supplier" });
    return true;
  }, [mutate]);

  const snoozeRow = useCallback(
    (id: string) => patchRow(id, { snoozed: true }),
    [patchRow],
  );

  const unsnoozeRow = useCallback(
    (id: string) => patchRow(id, { snoozed: false }),
    [patchRow],
  );

  const setStartBalance = useCallback(
    (n: number) => {
      mutate((s) => ({ ...s, startBalance: n }));
    },
    [mutate],
  );

  const addFixedCost = useCallback(
    (afterId?: string) => {
      const id = newId();
      const filter = costCenterFilterRef.current;
      const cost: FixedCost = {
        id,
        description: "",
        category: "",
        amount: 0,
        dayOfMonth: 1,
        occurrence: "monthly",
        startDate: null,
        endDate: null,
        costCenterId: filter === "all" || filter === "none" ? null : filter,
        startMonth: null,
      };
      mutate((s) => {
        const fixedCosts = [...s.fixedCosts];
        if (afterId) {
          const idx = fixedCosts.findIndex((c) => c.id === afterId);
          if (idx >= 0) fixedCosts.splice(idx + 1, 0, cost);
          else fixedCosts.push(cost);
        } else {
          fixedCosts.push(cost);
        }
        return { ...s, fixedCosts };
      });
    },
    [mutate],
  );

  const patchFixedCost = useCallback(
    (id: string, patch: Partial<FixedCost>) => {
      if ("costCenterId" in patch) {
        setPinnedCostIds((prev) =>
          prev.has(id) ? prev : new Set(prev).add(id),
        );
      }
      mutate((s) => ({
        ...s,
        fixedCosts: s.fixedCosts.map((c) =>
          c.id === id ? { ...c, ...patch } : c,
        ),
      }));
    },
    [mutate],
  );

  const deleteFixedCost = useCallback(
    (id: string) => {
      mutate((s) => ({
        ...s,
        fixedCosts: s.fixedCosts.filter((c) => c.id !== id),
      }));
    },
    [mutate],
  );

  const addCostCenter = useCallback(
    (name: string) => {
      const center: CostCenter = { id: newId(), name };
      mutate((s) => ({ ...s, costCenters: [...s.costCenters, center] }));
    },
    [mutate],
  );

  const renameCostCenter = useCallback(
    (id: string, name: string) => {
      mutate((s) => ({
        ...s,
        costCenters: s.costCenters.map((c) =>
          c.id === id ? { ...c, name } : c,
        ),
      }));
    },
    [mutate],
  );

  const deleteCostCenter = useCallback(
    (id: string) => {
      const s = stateRef.current;
      const index = s?.costCenters.findIndex((c) => c.id === id) ?? -1;
      if (!s || index < 0) return;
      const center = s.costCenters[index]!;
      const assigned = new Set(
        s.fixedCosts.filter((c) => c.costCenterId === id).map((c) => c.id),
      );
      mutate((s) => ({
        ...s,
        costCenters: s.costCenters.filter((c) => c.id !== id),
        fixedCosts: s.fixedCosts.map((c) =>
          c.costCenterId === id ? { ...c, costCenterId: null } : c,
        ),
      }));
      const restore = () =>
        mutate((s) => {
          if (s.costCenters.some((c) => c.id === id)) return s;
          const costCenters = [...s.costCenters];
          costCenters.splice(Math.min(index, costCenters.length), 0, center);
          return {
            ...s,
            costCenters,
            fixedCosts: s.fixedCosts.map((c) =>
              assigned.has(c.id) && c.costCenterId === null
                ? { ...c, costCenterId: id }
                : c,
            ),
          };
        });
      toast(`${center.name} borttaget`, {
        action: { label: "Ångra", onClick: restore },
      });
    },
    [mutate],
  );

  const changeCostCenterFilter = useCallback(
    (filter: CostCenterFilterValue) => {
      setCostCenterFilter(filter);
      setPinnedCostIds(new Set());
    },
    [],
  );

  const importFile = useCallback(async (file: File) => {
    const rows = stateRef.current?.rows ?? [];
    try {
      const plan = await planImportFromText(await file.text(), rows);
      setPendingImport({ fileName: file.name, plan });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte läsa filen");
    }
  }, []);

  const confirmImport = useCallback(() => {
    const pending = pendingImport;
    if (!pending) return;
    const add = pending.plan.add;
    mutate((s) => ({ ...s, rows: [...s.rows, ...add] }));
    setPendingImport(null);
    toast.success(
      `${add.length} rad${add.length === 1 ? "" : "er"} importerade`,
    );
  }, [mutate, pendingImport]);

  const confirmFixedCostImport = useCallback(
    (add: Row[], month: string) => {
      mutate((s) => ({ ...s, rows: [...s.rows, ...add] }));
      setImportingFixedCosts(false);
      toast.success(
        `${add.length} kostnad${add.length === 1 ? "" : "er"} inlagd${add.length === 1 ? "" : "a"} för ${month}`,
      );
    },
    [mutate],
  );

  const requestSwitch = useCallback(
    (id: string) => {
      if (id === companyId) return;
      setPendingSwitch(id);
      forceSave();
    },
    [companyId, forceSave],
  );

  const createCompany = useCallback(
    async (name: string) => {
      const company = await onCreateCompany(name);
      setManagingCompanies(false);
      requestSwitch(company.id);
    },
    [onCreateCompany, requestSwitch],
  );

  const handleAdd = useCallback(() => {
    if (viewRef.current === "kostnader") addFixedCost();
    else addRow();
  }, [addFixedCost, addRow]);

  useLayoutEffect(() => {
    const el = topRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty(
        "--app-top-h",
        `${el.offsetHeight}px`,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [state]);

  useLayoutEffect(() => {
    if (!pendingFocus) return;
    if (pendingFocus.kind === "startBalance") {
      const inp = document.getElementById(
        "start-balance-input",
      ) as HTMLInputElement | null;
      inp?.focus();
      inp?.select();
    } else {
      focusRowField(pendingFocus.rowId, pendingFocus.field);
    }
    setPendingFocus(null);
  }, [pendingFocus, state]);

  const enableInInputs = { enableOnFormTags: true as const };

  useHotkeys(
    "meta+s, ctrl+s",
    (e) => {
      e.preventDefault();
      forceSave();
    },
    enableInInputs,
    [forceSave],
  );

  useHotkeys(
    "meta+enter, ctrl+enter",
    (e) => {
      if (modalOpenRef.current) return;
      e.preventDefault();
      if (viewRef.current === "kostnader") {
        const activeTr = (document.activeElement as Element | null)?.closest?.(
          "tr.fixed-cost-row",
        ) as HTMLElement | null;
        const currentId = activeTr?.dataset.costId;
        addFixedCost(currentId);
        return;
      }
      const activeTr = (document.activeElement as Element | null)?.closest?.(
        "tr.row",
      ) as HTMLElement | null;
      const currentId = activeTr?.dataset.rowId;
      const current = currentId
        ? stateRef.current?.rows.find((r) => r.id === currentId)
        : null;
      if (current) addRow(current.dueDate, current.id);
      else addRow();
    },
    enableInInputs,
    [addRow, addFixedCost],
  );

  useHotkeys(
    "meta+backspace, ctrl+backspace, meta+delete, ctrl+delete",
    (e) => {
      if (modalOpenRef.current) return;
      const ae = document.activeElement as HTMLElement | null;
      const activeTr = ae?.closest?.("tr.row") as HTMLElement | null;
      if (!activeTr) return;
      if (activeTr.classList.contains("fixed-cost-row")) {
        const id = activeTr.dataset.costId;
        if (!id) return;
        e.preventDefault();
        deleteFixedCost(id);
        return;
      }
      e.preventDefault();
      const field = fieldFromElement(ae) ?? "supplier";
      const target = (nextSiblingRow(activeTr) ??
        prevSiblingRow(activeTr)) as HTMLElement | null;
      const fallbackId = target?.dataset.rowId;
      const id = activeTr.dataset.rowId;
      if (id) deleteRow(id);
      if (fallbackId) {
        setPendingFocus({ kind: "row", rowId: fallbackId, field });
      }
    },
    enableInInputs,
    [deleteRow, deleteFixedCost],
  );

  useHotkeys(
    "meta+z, ctrl+z",
    (e) => {
      if (modalOpenRef.current || undoRef.current.length === 0) return;
      e.preventDefault();
      undoDelete();
    },
    [undoDelete],
  );

  useHotkeys(
    "escape",
    () => {
      const ae = document.activeElement as HTMLInputElement | null;
      if (ae?.tagName === "INPUT" || ae?.tagName === "TEXTAREA") ae?.blur();
    },
    enableInInputs,
  );

  const activeRows = useMemo(
    () => state?.rows.filter((r) => !r.snoozed) ?? [],
    [state],
  );
  const snoozedRows = useMemo(
    () => state?.rows.filter((r) => r.snoozed) ?? [],
    [state],
  );

  const fixedCosts = state?.fixedCosts;
  const costCenters = state?.costCenters;

  const filteredCenter =
    costCenterFilter === "all" || costCenterFilter === "none"
      ? null
      : (costCenters?.find((c) => c.id === costCenterFilter) ?? null);
  const activeFilter: CostCenterFilterValue =
    costCenterFilter === "none" ? "none" : (filteredCenter?.id ?? "all");
  costCenterFilterRef.current = activeFilter;
  const filterLabel =
    activeFilter === "none" ? "Utan kostnadsställe" : filteredCenter?.name;

  const filterWant = activeFilter === "none" ? null : activeFilter;

  const visibleFixedCosts = useMemo(() => {
    const all = fixedCosts ?? [];
    if (activeFilter === "all") return all;
    return all.filter(
      (c) => c.costCenterId === filterWant || pinnedCostIds.has(c.id),
    );
  }, [fixedCosts, activeFilter, filterWant, pinnedCostIds]);

  const fixedCostsInFilter = useMemo(() => {
    const all = fixedCosts ?? [];
    if (activeFilter === "all") return all;
    return all.filter((c) => c.costCenterId === filterWant);
  }, [fixedCosts, activeFilter, filterWant]);

  const projection = useMemo(() => {
    if (!state) return 0;
    let bal = Number(state.startBalance) || 0;
    for (const r of activeRows) bal += Number(r.amount) || 0;
    return bal;
  }, [state, activeRows]);

  const invalidDates = useMemo(
    () => (state ? hasInvalidDates(state) : false),
    [state],
  );

  useEffect(() => {
    if (pendingSwitch === null || saving) return;
    if (!dirty) {
      onSwitchCompany(pendingSwitch);
      return;
    }
    if (invalidDates) {
      toast.error("Rätta ogiltiga datum innan du byter företag");
      setPendingSwitch(null);
    }
  }, [pendingSwitch, saving, dirty, invalidDates, onSwitchCompany]);

  const status: SaveStatus = saving
    ? "saving"
    : dirty && invalidDates
      ? "invalid"
      : dirty
        ? "dirty"
        : "saved";

  if (!state) return null;

  return (
    <>
      <div className="app-top" ref={topRef}>
        <Header
          version={state.version}
          status={status}
          canSave={dirty}
          view={view}
          onViewChange={onViewChange}
          companies={companies}
          companyId={companyId}
          onCompanyChange={requestSwitch}
          onManageCompanies={() => setManagingCompanies(true)}
          manageCompaniesRef={manageCompaniesRef}
          onAdd={handleAdd}
          onSave={forceSave}
          onImportFile={importFile}
          onImportFixedCosts={() => setImportingFixedCosts(true)}
        />
        {view === "likviditet" ? (
          <StartBalance
            value={state.startBalance}
            projection={projection}
            onChange={setStartBalance}
            onEnter={() => addRow()}
          />
        ) : (
          <div className="fixed-costs-bar">
            <CostCenterFilter
              costCenters={state.costCenters}
              value={activeFilter}
              onChange={changeCostCenterFilter}
              onManage={() => setManagingCostCenters(true)}
              manageRef={manageCostCentersRef}
            />
            <FixedCostsSummary
              fixedCosts={visibleFixedCosts}
              allFixedCosts={state.fixedCosts}
              filterLabel={filterLabel ?? null}
            />
          </div>
        )}
      </div>
      {view === "likviditet" ? (
        <>
          <LedgerTable
            rows={activeRows}
            startBalance={state.startBalance}
            focusedDateRowId={focusedDateRowId}
            setFocusedDateRowId={setFocusedDateRowId}
            onPatch={patchRow}
            onDelete={deleteRow}
            onSnooze={snoozeRow}
            onAddAfter={addRow}
          />
          {snoozedRows.length > 0 && (
            <SnoozedTable
              rows={snoozedRows}
              onPatch={patchRow}
              onDelete={deleteRow}
              onUnsnooze={unsnoozeRow}
            />
          )}
        </>
      ) : (
        <>
          <FixedCostsTable
            fixedCosts={visibleFixedCosts}
            costCenters={state.costCenters}
            onPatch={patchFixedCost}
            onDelete={deleteFixedCost}
            onAddAfter={addFixedCost}
          />
        </>
      )}
      {pendingImport && (
        <ImportDialog
          fileName={pendingImport.fileName}
          plan={pendingImport.plan}
          onConfirm={confirmImport}
          onCancel={() => setPendingImport(null)}
        />
      )}
      {importingFixedCosts && (
        <FixedCostImportDialog
          fixedCosts={fixedCostsInFilter}
          rows={state.rows}
          filterLabel={filterLabel ?? null}
          onConfirm={confirmFixedCostImport}
          onCancel={() => setImportingFixedCosts(false)}
        />
      )}
      {managingCostCenters && (
        <CostCentersDialog
          costCenters={state.costCenters}
          fixedCosts={state.fixedCosts}
          onAdd={addCostCenter}
          onRename={renameCostCenter}
          onDelete={deleteCostCenter}
          onClose={() => {
            setManagingCostCenters(false);
            manageCostCentersRef.current?.focus();
          }}
        />
      )}
      {managingCompanies && (
        <CompaniesDialog
          companies={companies}
          onCreate={createCompany}
          onRename={onRenameCompany}
          onClose={() => {
            setManagingCompanies(false);
            manageCompaniesRef.current?.focus();
          }}
        />
      )}
      <Toaster position="bottom-right" richColors />
    </>
  );
}
